<template>
  <div class="user-profile">
    <h1 class="page-title">用户资料</h1>
    <el-card class="profile-card">
      <div class="profile-header">
        <div class="avatar-section">
          <div class="avatar-wrapper">
            <img :src="avatarUrlRef" alt="用户头像" class="user-avatar" />
            <input
              type="file"
              ref="fileInput"
              style="display: none"
              accept="image/*"
              @change="handleFileChange"
            />
          </div>
          <el-button 
            type="primary" 
            class="upload-btn" 
            size="small"
            @click="triggerFileSelect"
            :loading="uploading"
          >
            {{ uploading ? '上传中...' : '更换头像' }}
          </el-button>
          <div class="user-name-wrapper">
            <h2>{{ user?.username || '未知用户' }}</h2>
          </div>
          <VipBadge
            v-if="user"
            :level="user.vip"
            :expires-at="user.vipExpiresAt"
          />
        </div>
        
        <div class="info-section">
          <div class="info-columns">
            <div class="info-group">
              <div class="info-item">
                <span class="label">账号：</span>
                <span class="value">{{ user?.account }}</span>
              </div>
              <div class="info-item">
                <span class="label">用户名：</span>
                <span class="value">{{ user?.username }}</span>
              </div>
              <div class="info-item">
                <span class="label">邮箱：</span>
                <span class="value">{{ user?.email }}</span>
              </div>
              <div class="info-item">
                <span class="label">手机：</span>
                <span class="value">{{ user?.phone || '未提供' }}</span>
              </div>
            </div>
            <div class="info-group">
              <div class="info-item">
                <span class="label">VIP状态：</span>
                <span class="value">
                  <template v-if="user?.vip > 0">
                    <el-tag size="small" type="success" effect="light">
                      VIP - {{ formatDate(user.vipExpiresAt) }} 到期
                    </el-tag>
                  </template>
                  <template v-else>
                    <el-tag size="small" type="info" effect="light">普通用户</el-tag>
                    <el-button 
                      type="primary" 
                      size="small" 
                      class="upgrade-btn"
                      @click="handleUpgradeClick"
                    >
                      立即升级
                    </el-button>
                  </template>
                </span>
              </div>
              <div class="info-item">
                <span class="label">最后登录：</span>
                <span class="value">{{ formatDate(user?.lastLoginAt) }}</span>
              </div>
              <div class="info-item">
                <span class="label">注册时间：</span>
                <span class="value">{{ formatDate(user?.createdAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-card>
    
    <div class="section-divider"></div>
    
    <h1 class="page-title" id="settings">设置</h1>
    <el-card class="profile-card settings-card">
      <div class="settings-content">
        <div class="settings-group">
          <div class="settings-item">
            <div class="setting-header">
              <span class="label">界面主题</span>
              <span class="description">选择您喜欢的界面主题风格</span>
            </div>
            <div class="setting-control">
              <el-switch
                v-model="isDarkTheme"
                class="theme-switch"
                :active-icon="Moon"
                :inactive-icon="Sunny"
                inline-prompt
                style="--el-switch-on-color: #494949; --el-switch-width: 60px"
              />
            </div>
          </div>

          <el-divider />
          
          <div class="settings-item">
            <div class="setting-header">
              <span class="label">通知提醒</span>
              <span class="description">开启后将收到系统通知和重要提醒</span>
            </div>
            <div class="setting-control">
              <el-switch
                v-model="notifications"
                inline-prompt
                :active-icon="Bell"
                :inactive-icon="BellSlash"
                class="notification-switch"
                style="--el-switch-on-color: #67c23a; --el-switch-width: 60px"
              />
            </div>
          </div>

          <el-divider />
          
          <div class="settings-item">
            <div class="setting-header">
              <span class="label">隐私政策</span>
              <span class="description">了解我们如何保护您的隐私</span>
            </div>
            <div class="setting-control">
              <el-button 
                plain
                size="default"
                @click="router.push('/privacy-policy')"
              >
                查看详情
              </el-button>
            </div>
          </div>

          <el-divider />
          
          <div class="settings-item">
            <div class="setting-header">
              <span class="label">用户协议</span>
              <span class="description">查看使用条款和服务协议</span>
            </div>
            <div class="setting-control">
              <el-button 
                plain
                size="default"
                @click="router.push('/user-agreement')"
              >
                查看详情
              </el-button>
            </div>
          </div>

          <el-divider />
          
          <div class="settings-item danger-zone">
            <div class="setting-header">
              <span class="label">账号注销</span>
              <span class="description danger-text">注销后账号七日内可恢复，七日后会删除所有数据，请谨慎操作</span>
            </div>
            <div class="setting-control">
              <el-button 
                type="danger" 
                plain
                class="delete-btn"
                @click="showDeleteConfirm"
              >
                注销账号
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 注销确认对话框 -->
    <el-dialog
      v-model="deleteDialogVisible"
      title="注销账号确认"
      width="400px"
      :close-on-click-modal="false"
    >
      <div class="delete-account-dialog">
        <p class="warning-text">注意：账号注销后将无法恢复，请谨慎操作！</p>
        <div class="verification-section" v-if="showVerificationInput">
          <el-input
            v-model="verificationCode"
            placeholder="请输入邮箱验证码"
            maxlength="6"
            class="verification-input"
          >
            <template #append>
              <el-button 
                :disabled="countdown > 0" 
                @click="sendVerificationCode"
              >
                {{ countdown > 0 ? `${countdown}s后重试` : '获取验证码' }}
              </el-button>
            </template>
          </el-input>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="deleteDialogVisible = false">取消</el-button>
          <el-button
            type="danger"
            :disabled="!verificationCode"
            :loading="deleting"
            @click="confirmDeleteAccount"
          >
            确认注销
          </el-button>
        </span>
      </template>
    </el-dialog>
    
    <!-- VIP兑换对话框 -->
    <VipRedeemDialog v-model:visible="vipRedeemDialogVisible" @redeemed="handleVipRedeemed" />
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from '@/store/modules/user';
import { ElMessage } from 'element-plus';
import defaultAvatar from '@/assets/defualt.jpg';
import VipBadge from '@/components/VipBadge.vue';
import VipRedeemDialog from '@/components/VipRedeemDialog.vue';
import axios from 'axios';
import { resourceApi } from '@/services/api';
import { config } from '@/config/config';
import { Sunny, Moon, Monitor, Check, Bell, BellSlash, ArrowRight } from '@element-plus/icons-vue';

interface User {
  id: number;
  account: string;
  username: string;
  email: string;
  phone: string | null;
  avatarUrl: string | null;
  vip: number;
  vipExpiresAt: string | null;
  status: string;
  lastLoginAt: string;
  createdAt: string;
}

const router = useRouter();
const userStore = useUserStore();
const user = computed(() => userStore.userInfo);
const fileInput = ref<HTMLInputElement | null>(null);
const uploading = ref(false);
const avatarUrlRef = ref<string>(defaultAvatar);
const route = useRoute();
const defaultLanguage = ref('zh');
const isDarkTheme = ref(false);
const theme = computed({
  get: () => isDarkTheme.value ? 'dark' : 'light',
  set: (value) => {
    isDarkTheme.value = value === 'dark';
  }
});
const notifications = ref(true);

// 添加基础URL配置 - 使用config
const API_BASE_URL = 'https://aivitahelper.xyz:8081';

const formatDate = (date: string | undefined) => {
  if (!date) return '未知';
  return new Date(date).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const handleUpgradeClick = () => {
  vipRedeemDialogVisible.value = true;
};

const vipRedeemDialogVisible = ref(false);

// 处理VIP兑换成功
const handleVipRedeemed = () => {
  // 刷新用户信息
  userStore.fetchUserInfo();
};

// 处理头像URL
const loadAvatar = async () => {
  if (!user.value?.avatarUrl) {
    avatarUrlRef.value = defaultAvatar;
    return;
  }
  
  // 如果已经是完整URL则直接返回
  if (user.value.avatarUrl.startsWith('http')) {
    avatarUrlRef.value = user.value.avatarUrl;
    return;
  }

  try {
    // 获取图片数据
    const response = await resourceApi.get(user.value.avatarUrl, {
      responseType: 'blob'
    });
    
    // 将blob转换为base64
    const reader = new FileReader();
    reader.onloadend = () => {
      avatarUrlRef.value = reader.result as string;
    };
    reader.readAsDataURL(response.data);
  } catch (error) {
    console.error('加载头像失败:', error);
    avatarUrlRef.value = defaultAvatar;
  }
};

// 监听用户信息变化
watch(() => user.value?.avatarUrl, (newAvatarUrl) => {
  if (newAvatarUrl) {
    loadAvatar();
  }
}, { immediate: true });

const triggerFileSelect = () => {
  fileInput.value?.click();
};

const handleFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  
  if (!file) return;
  
  // 验证文件类型
  if (!file.type.startsWith('image/')) {
    ElMessage.error('请选择图片文件');
    return;
  }
  
  // 验证文件大小（限制为2MB）
  if (file.size > 2 * 1024 * 1024) {
    ElMessage.error('图片大小不能超过2MB');
    return;
  }
  
  uploading.value = true;
  const formData = new FormData();
  formData.append('file', file);
  
  try {
    const response = await axios.post(`${API_BASE_URL}/api/user/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    if (response.data.code === 200) {
      // 更新用户信息以获取新的头像URL
      await userStore.fetchUserInfo();
      ElMessage.success('头像更新成功');
    } else {
      throw new Error(response.data.message || '上传失败');
    }
  } catch (error: any) {
    ElMessage.error(error.message || '头像上传失败，请重试');
  } finally {
    uploading.value = false;
    // 清除文件选择，允许选择相同文件
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};

const scrollToSection = async () => {
  if (route.hash) {
    await nextTick();
    const element = document.getElementById(route.hash.slice(1));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  } else {
    // 如果没有hash，滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

// 监听路由变化
watch(() => route.fullPath, () => {
  scrollToSection();
});

// 账号注销相关
const deleteDialogVisible = ref(false);
const showVerificationInput = ref(false);
const verificationCode = ref('');
const countdown = ref(0);
const deleting = ref(false);

// 显示注销确认对话框
const showDeleteConfirm = () => {
  deleteDialogVisible.value = true;
  showVerificationInput.value = true;
  verificationCode.value = '';
};

// 发送验证码
const sendVerificationCode = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/user/send-deletion-code`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    
    if (response.data.success) {
      ElMessage.success('验证码已发送到您的邮箱');
      // 开始倒计时
      countdown.value = 60;
      const timer = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    }
  } catch (error: any) {
    ElMessage.error(error.response?.data?.message || '发送验证码失败');
  }
};

// 确认注销账号
const confirmDeleteAccount = async () => {
  if (!verificationCode.value) {
    ElMessage.warning('请输入验证码');
    return;
  }

  try {
    deleting.value = true;
    const response = await axios.post(
      `${API_BASE_URL}/api/user/delete-account?verificationCode=${verificationCode.value}`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    
    if (response.data.success) {
      ElMessage.success('账号已成功注销');
      // 清除用户信息和token
      localStorage.removeItem('token');
      userStore.clearUserInfo();
      // 跳转到登录页
      router.push('/login');
    }
  } catch (error: any) {
    ElMessage.error(error.response?.data?.message || '注销账号失败');
  } finally {
    deleting.value = false;
  }
};

onMounted(() => {
  userStore.fetchUserInfo();
  scrollToSection();
});
</script>

<style lang="scss" scoped>
.user-profile {
  width: 85%;
  margin: 0 auto;
  padding: 20px;
  min-height: 120vh;
  
  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #333;
    margin: 40px 0 20px;
    padding-left: 12px;
    border-left: 4px solid #333;
    
    &:first-child {
      margin-top: 0;
    }
  }
  
  .profile-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    margin-bottom: 60px;
    
    .profile-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 24px;
      
      .avatar-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        min-width: 200px;
        
        .avatar-wrapper {
          position: relative;
          
          .user-avatar {
            width: 120px;
            height: 120px;
            border-radius: 60px;
            object-fit: cover;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            transition: transform 0.3s ease;
            
            &:hover {
              transform: scale(1.05);
            }
          }
        }
        
        .user-name-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          
          h2 {
            font-size: 20px;
            font-weight: 600;
            color: #333;
            margin: 8px 0 4px;
          }
        }
        
        .upload-btn {
          width: 100px;
          border-radius: 20px !important;
        }
      }
      
      .info-section {
        flex: 1;
        margin-left: 48px;
        
        .info-columns {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 32px;
          
          .info-group {
            .info-item {
              margin-bottom: 16px;
              display: flex;
              align-items: center;
              
              .label {
                width: 90px;
                color: #666;
                font-size: 15px;
              }
              
              .value {
                flex: 1;
                color: #333;
                font-size: 15px;
                font-weight: 500;
                display: flex;
                align-items: center;
                gap: 8px;
                
                .el-tag {
                  margin-left: 8px;
                }
                
                .upgrade-btn {
                  margin-left: 12px;
                }
              }
            }
          }
        }
      }
    }
  }
  
  .section-divider {
    height: 1px;
    background: linear-gradient(to right, transparent, #e0e0e0, transparent);
    margin: 40px 0;
  }
}

.settings-card {
  margin-top: 40px;
  
  .settings-content {
    padding: 32px;
    
    .settings-group {
      .settings-item {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 24px 0;
        
        .setting-header {
          flex: 1;
          padding-right: 40px;
          
          .label {
            display: block;
            font-size: 16px;
            font-weight: 500;
            color: #333;
            margin-bottom: 4px;
          }
          
          .description {
            font-size: 14px;
            color: #666;
            
            &.danger-text {
              color: #f56c6c;
            }
          }
        }
        
        .setting-control {
          width: 140px;
          display: flex;
          justify-content: flex-start;
          
          .theme-switch,
          .notification-switch {
            transform: scale(1.1);
          }
          
          .el-button {
            border-radius: 20px;
            padding: 8px 24px;
            width: 100%;
            font-size: 14px;
          }
        }
        
        &.danger-zone {
          margin-bottom: 0;
        }
      }
      
      .el-divider {
        margin: 0;
      }
    }
  }
}

.delete-account-dialog {
  .warning-text {
    color: #f56c6c;
    font-size: 14px;
    margin-bottom: 20px;
    text-align: center;
  }
  
  .verification-section {
    .verification-input {
      .el-input-group__append {
        .el-button {
          padding: 0 15px;
        }
      }
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}

// 改进移动端适配
@media (max-width: 768px) {
  .user-profile {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background-color: #f5f5f7 !important;
    
    .page-title {
      font-size: 1.2rem !important;
      margin: 10px auto !important;
      width: calc(100% - 20px) !important;
      padding: 10px !important;
      background-color: #fff !important;
      border-radius: 12px !important;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
      border-left-width: 3px !important;
    }
    
    .profile-card {
      margin: 10px auto !important;
      padding: 0 !important;
      border-radius: 12px !important;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
      background-color: #fff !important;
      width: calc(100% - 20px) !important;
      overflow: hidden !important;
      
      .el-card__body {
        padding: 15px !important;
      }
      
      .profile-header {
        flex-direction: row !important;
        padding: 0 !important;
        align-items: flex-start !important;
        
        .avatar-section {
          align-items: center !important;
          width: 35% !important;
          min-width: auto !important;
          margin-right: 10px !important;
          margin-bottom: 0 !important;
          
          .user-avatar {
            width: 60px !important;
            height: 60px !important;
            border-radius: 30px !important;
          }
          
          .user-name-wrapper h2 {
            font-size: 0.9rem !important;
            margin: 3px 0 2px !important;
          }
          
          .upload-btn {
            width: 60px !important;
            font-size: 0.7rem !important;
            padding: 2px 0 !important;
            height: 22px !important;
            line-height: 1 !important;
          }
        }
        
        .info-section {
          margin-left: 0 !important;
          width: 65% !important;
          padding-left: 5px !important;
          
          .info-columns {
            display: block !important;
            grid-template-columns: none !important;
            gap: 5px !important;
            
            .info-group {
              margin-bottom: 5px !important;
            }
            
            .info-item {
              margin-bottom: 4px !important;
              
              .label {
                width: 60px !important;
                min-width: 60px !important;
                font-size: 0.75rem !important;
              }
              
              .value {
                font-size: 0.75rem !important;
                word-break: break-all !important;
                
                .el-tag {
                  font-size: 0.7rem !important;
                  padding: 0 4px !important;
                  height: 18px !important;
                  line-height: 18px !important;
                }
                
                .upgrade-btn {
                  margin-left: 4px !important;
                  padding: 0 4px !important;
                  font-size: 0.7rem !important;
                  height: 18px !important;
                }
              }
            }
          }
        }
      }
    }
    
    .section-divider {
      margin: 20px 0 !important;
    }
    
    .settings-card {
      margin: 10px auto 30px auto !important;
      
      .settings-content {
        padding: 10px !important;
        
        .settings-group {
          .settings-item {
            padding: 8px 0 !important;
            
            .setting-header {
              padding-right: 5px !important;
              margin-bottom: 8px !important;
              
              .label {
                font-size: 0.8rem !important;
                margin-bottom: 0 !important;
              }
              
              .description {
                font-size: 0.7rem !important;
              }
            }
            
            .setting-control {
              width: 70px !important;
              
              .theme-switch,
              .notification-switch {
                transform: scale(0.7) !important;
              }
              
              .el-button {
                font-size: 0.7rem !important;
                padding: 2px 6px !important;
                height: auto !important;
                width: auto !important;
              }
            }
          }
        }
      }
    }
  }
  
  .el-dialog {
    width: 90% !important;
    max-width: 400px !important;
    
    .el-dialog__header {
      padding: 15px !important;
    }
    
    .el-dialog__body {
      padding: 15px !important;
    }
    
    .el-dialog__footer {
      padding: 15px !important;
    }
  }
}
</style> 